// @ts-nocheck
import { Edit3, Info } from "lucide-react";
import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
  type RefObject,
} from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

import { HawkeyeMatrixBubbleChart } from "./components/hawkeye/HawkeyeMatrixBubbleChart";
import { HawkeyeMetricsBubbleChart } from "./components/hawkeye/HawkeyeMetricsBubbleChart";
import { Card, CardContent } from "./components/ui/card";
import type {
  HawkeyeChartMetricsProps,
  HawkeyeMetricsAccountsInput,
} from "./hooks/useHawkeyeMetrics";
import {
  buildAccountMatrixData,
  emptyDigestState,
  fetchHawkeyeDigestState,
  getAvailableCategories,
  getDefaultMetricsConfiguration,
  getOrderedCategories,
  loadMetricsConfigurationFromApi,
  normalizeAccounts,
  resolveDigestDateRange,
} from "./hooks/hawkeyeMetricsCore";
import Pagination from "../Components/Pagination";
import useXFactorKpiGenerate from "../Hooks/useXFactorKpiGenerate";
import {
  hasAlwaysOnRowRevealCompleted,
  markAlwaysOnRowRevealCompleted,
} from "../Pages/AlwaysOnChannel/Dashboard/alwaysOnRowReveal";
import SyntheticDataModeBanner from "../Components/SyntheticDataModeBanner";
import { useOrgSyntheticDataToggle } from "../Hooks/useOrgSyntheticDataToggle";
import { useAppDispatch, useAppSelector } from "../Store/Hooks";
import { fetchMetricsTimePeriods } from "../Store/Reducers/MetricsSlice";
import { ROLES, isRole } from "../Utils/Constants/RoleConstants";
import {
  STORAGE_KEYS,
  getFromLocalStorage,
} from "../Utils/Helper/LocalStorage";

const DEFAULT_KPI_PAGE_SIZE = 20;
const KPI_PAGE_SIZE_OPTIONS = [5, 10, 15, 20, 25, 50, 100];

const accountNameAsc = (left: { account_name?: string }, right: { account_name?: string }) =>
  String(left?.account_name || "").localeCompare(
    String(right?.account_name || ""),
    undefined,
    { sensitivity: "base" }
  );

export type HawkeyeDataSource = "hawkeye" | "digitalocean";

export interface HawkeyePageProps {
  accounts?: Array<{
    id: string;
    name: string;
    city?: string;
    country?: string;
    region?: string;
    latitude?: number;
    longitude?: number;
    primary_sponsors?: string | null;
    primary_sponsors_id?: string | null;
    secondary_sponsors?: string | null;
    secondary_sponsors_id?: string | null;
  }>;
  watchlistId?: string | null;
  measurementDate?: string | null;
  /**
   * Account Health data-source toggle. Both modes share Hawkeye digest dots
   * (timeseries + org weightage). digitalocean adds native Generate/briefing.
   */
  dataSource?: HawkeyeDataSource;
  /** Ingest day (`created_at::date`) for digitalocean Generate/Regenerate. */
  ingestDate?: string | null;
  /** When set (digitalocean), overrides Hawkeye's internal period digest range. */
  digestFromDate?: string | null;
  digestToDate?: string | null;
  highlightedAccountIds?: string[];
  onHighlightedAccountIdsChange?: (accountIds: string[]) => void;
  onSaveAsWatchlist?: (accountIds: string[]) => void;
  /**
   * When true (default), show all accounts with no-data rows at the bottom.
   * When false, show all accounts in alphabetical order.
   */
  showUnavailableKpiData?: boolean;
  /** Reports how many accounts have / lack Enterprise KPI data. */
  onKpiDataCountsChange?: (counts: {
    available: number;
    unavailable: number;
    loading: boolean;
  }) => void;
  /** Account IDs with no Enterprise KPI data (align left Accounts list sort). */
  onAccountsWithoutKpiDataChange?: (accountIds: string[]) => void;
  /** Briefing panel bounds for floating compare bar (Always On Intelligence). */
  selectionPanelRef?: RefObject<HTMLElement | null>;
  /**
   * Optional controls rendered on the toolbar right (before Configure KPIs),
   * e.g. Granularity/Period from EnterpriseKpisPanel for digitalocean.
   */
  toolbarEnd?: React.ReactNode;
}

const HawkeyePage: React.FC<HawkeyePageProps> = ({
  accounts = [],
  watchlistId = null,
  measurementDate = null,
  dataSource = "hawkeye",
  ingestDate = null,
  digestFromDate: digestFromDateProp = null,
  digestToDate: digestToDateProp = null,
  highlightedAccountIds = [],
  onHighlightedAccountIdsChange,
  onSaveAsWatchlist,
  showUnavailableKpiData = true,
  onKpiDataCountsChange,
  onAccountsWithoutKpiDataChange,
  selectionPanelRef: selectionPanelRefProp,
  toolbarEnd = null,
}) => {
  const isDigitalocean = dataSource === "digitalocean";
  const { dataModeKey } = useOrgSyntheticDataToggle();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { orgSlug } = useParams<{ orgSlug?: string }>();
  const { user } = useAppSelector(state => state.authentication);
  const loggedInUser = getFromLocalStorage(STORAGE_KEYS.LOGGED_IN_USER);
  const canConfigureKpis = useMemo(() => {
    const userRole = user?.user?.role || loggedInUser?.role;
    const executiveOps =
      loggedInUser?.executiveOps || user?.user?.executive_ops || false;
    return (
      isRole(userRole, ROLES.VENDOR_ADMIN) ||
      isRole(userRole, ROLES.C_LEVEL) ||
      isRole(userRole, ROLES.EXECUTIVE_SPONSOR_LEAD) ||
      Boolean(executiveOps)
    );
  }, [loggedInUser, user]);
  const internalPanelRef = useRef<HTMLDivElement>(null);
  const selectionPanelRef = selectionPanelRefProp ?? internalPanelRef;
  type Granularity = "weekly" | "monthly" | "quarterly";
  const [selectedKPIs, setSelectedKPIs] = useState<string[]>([]);
  const [levelView, setLevelView] = useState<"categories" | "metrics">(
    "categories"
  );
  const [kpiUnavailableInfoOpen, setKpiUnavailableInfoOpen] = useState(false);
  const kpiUnavailableInfoCloseTimerRef = useRef<ReturnType<
    typeof setTimeout
  > | null>(null);
  const [granularity, setGranularity] = useState<Granularity>("monthly");
  const [periodBounds, setPeriodBounds] = useState<{
    minCreatedDate: string | null;
    maxCreatedDate: string | null;
  }>({ minCreatedDate: null, maxCreatedDate: null });
  const [briefedAccountIds, setBriefedAccountIds] = useState<Set<string>>(
    () => new Set()
  );
  const { generatingAccountId, generate } = useXFactorKpiGenerate();

  const openKpiUnavailableInfo = useCallback(() => {
    if (kpiUnavailableInfoCloseTimerRef.current) {
      clearTimeout(kpiUnavailableInfoCloseTimerRef.current);
      kpiUnavailableInfoCloseTimerRef.current = null;
    }
    setKpiUnavailableInfoOpen(true);
  }, []);

  const scheduleCloseKpiUnavailableInfo = useCallback(() => {
    if (kpiUnavailableInfoCloseTimerRef.current) {
      clearTimeout(kpiUnavailableInfoCloseTimerRef.current);
    }
    kpiUnavailableInfoCloseTimerRef.current = setTimeout(() => {
      setKpiUnavailableInfoOpen(false);
      kpiUnavailableInfoCloseTimerRef.current = null;
    }, 200);
  }, []);

  useEffect(() => {
    return () => {
      if (kpiUnavailableInfoCloseTimerRef.current) {
        clearTimeout(kpiUnavailableInfoCloseTimerRef.current);
      }
    };
  }, []);

  // Close the tip when the toggle is turned back on
  useEffect(() => {
    if (showUnavailableKpiData) {
      setKpiUnavailableInfoOpen(false);
    }
  }, [showUnavailableKpiData]);

  const toDate = (raw: string | null): Date | null => {
    if (!raw) return null;
    const [year, month, day] = raw.split("-").map(Number);
    if (!year || !month || !day) return null;
    return new Date(year, month - 1, day);
  };

  const formatDate = (date: Date) => {
    const yyyy = date.getFullYear();
    const mm = String(date.getMonth() + 1).padStart(2, "0");
    const dd = String(date.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  };

  useEffect(() => {
    const handleKPIChange = (event: CustomEvent) => {
      setSelectedKPIs(event.detail.kpis || []);
    };

    window.addEventListener(
      "hawkeyeKPIChange",
      handleKPIChange as EventListener
    );

    return () => {
      window.removeEventListener(
        "hawkeyeKPIChange",
        handleKPIChange as EventListener
      );
    };
  }, []);

  const loadTimePeriods = useCallback(async () => {
    try {
      const content = await dispatch(fetchMetricsTimePeriods()).unwrap();
      // Prefer measurement_date bounds — KPI values live on measurement_date,
      // not ingest created_at (Divum Aug metrics were imported in September).
      const minCreatedDate =
        content?.min_measurement_date ??
        content?.min_created_date ??
        null;
      const maxCreatedDate =
        content?.max_measurement_date ??
        content?.max_created_date ??
        null;

      setPeriodBounds({
        minCreatedDate: typeof minCreatedDate === "string" ? minCreatedDate : null,
        maxCreatedDate: typeof maxCreatedDate === "string" ? maxCreatedDate : null,
      });
    } catch (error) {
      console.warn("Failed to load metrics time periods:", error);
      setPeriodBounds({ minCreatedDate: null, maxCreatedDate: null });
    }
  }, [dispatch]);

  useEffect(() => {
    if (isDigitalocean) return;
    loadTimePeriods();
  }, [isDigitalocean, loadTimePeriods]);

  const periodOptions = useMemo(() => {
    const options: Array<{
      value: string;
      label: string;
      fromDate: string;
      toDate: string;
      measurementDate: string;
    }> = [];
    const fallbackToday = new Date();
    const maxDate =
      toDate(periodBounds.maxCreatedDate) ??
      new Date(
        fallbackToday.getFullYear(),
        fallbackToday.getMonth(),
        fallbackToday.getDate()
      );
    const minDate = toDate(periodBounds.minCreatedDate) ?? new Date(maxDate);
    if (minDate > maxDate) return [];

    if (granularity === "monthly") {
      let cursor = new Date(maxDate.getFullYear(), maxDate.getMonth(), 1);
      while (true) {
        const start = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
        const end = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0);
        if (end < minDate) break;
        const boundedStart = start < minDate ? minDate : start;
        const boundedEnd = end > maxDate ? maxDate : end;
        options.push({
          value: `${start.getFullYear()}-${String(start.getMonth() + 1).padStart(2, "0")}`,
          label: start.toLocaleString("en-US", {
            month: "long",
            year: "numeric",
          }),
          fromDate: formatDate(boundedStart),
          toDate: formatDate(boundedEnd),
          measurementDate: formatDate(boundedEnd),
        });
        cursor = new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1);
      }
    } else if (granularity === "weekly") {
      let weekEnd = new Date(maxDate);
      while (weekEnd >= minDate) {
        const weekStart = new Date(weekEnd);
        weekStart.setDate(weekEnd.getDate() - 6);
        const boundedStart = weekStart < minDate ? minDate : weekStart;
        const boundedEnd = weekEnd > maxDate ? maxDate : weekEnd;
        options.push({
          value: `wk-${formatDate(boundedStart)}-${formatDate(boundedEnd)}`,
          label: `${boundedStart.toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
          })} – ${boundedEnd.toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
            year: "numeric",
          })}`,
          fromDate: formatDate(boundedStart),
          toDate: formatDate(boundedEnd),
          measurementDate: formatDate(boundedEnd),
        });
        weekEnd = new Date(boundedStart);
        weekEnd.setDate(weekEnd.getDate() - 1);
      }
    } else {
      const currentQuarterStartMonth = Math.floor(maxDate.getMonth() / 3) * 3;
      let quarterStart = new Date(maxDate.getFullYear(), currentQuarterStartMonth, 1);
      while (true) {
        const quarterEnd = new Date(
          quarterStart.getFullYear(),
          quarterStart.getMonth() + 3,
          0
        );
        if (quarterEnd < minDate) break;
        const boundedStart = quarterStart < minDate ? minDate : quarterStart;
        const boundedEnd = quarterEnd > maxDate ? maxDate : quarterEnd;
        const quarterNumber = Math.floor(quarterStart.getMonth() / 3) + 1;
        options.push({
          value: `q-${quarterStart.getFullYear()}-${quarterNumber}`,
          label: `Q${quarterNumber} ${quarterStart.getFullYear()}`,
          fromDate: formatDate(boundedStart),
          toDate: formatDate(boundedEnd),
          measurementDate: formatDate(boundedEnd),
        });
        quarterStart = new Date(
          quarterStart.getFullYear(),
          quarterStart.getMonth() - 3,
          1
        );
      }
    }
    return options;
  }, [granularity, periodBounds.maxCreatedDate, periodBounds.minCreatedDate]);

  const [selectedPeriod, setSelectedPeriod] = useState<string>("");

  useEffect(() => {
    if (!periodOptions.length) return;
    setSelectedPeriod(prev =>
      periodOptions.some(option => option.value === prev)
        ? prev
        : periodOptions[0].value
    );
  }, [periodOptions]);

  const activePeriodOption = useMemo(() => {
    if (!periodOptions.length) return null;
    return (
      periodOptions.find(p => p.value === selectedPeriod) ?? periodOptions[0]
    );
  }, [periodOptions, selectedPeriod]);

  const effectiveMeasurementDate = useMemo(() => {
    if (isDigitalocean && measurementDate) return measurementDate;
    if (activePeriodOption) return activePeriodOption.measurementDate;
    return periodBounds.maxCreatedDate ?? formatDate(new Date());
  }, [
    isDigitalocean,
    measurementDate,
    activePeriodOption,
    periodBounds.maxCreatedDate,
  ]);

  const controlledDigestFrom =
    isDigitalocean && digestFromDateProp
      ? digestFromDateProp
      : activePeriodOption?.fromDate ?? null;
  const controlledDigestTo =
    isDigitalocean && digestToDateProp
      ? digestToDateProp
      : activePeriodOption?.toDate ?? null;

  const metricsAccountsInput = useMemo<HawkeyeMetricsAccountsInput>(
    () => ({
      selectedAccounts: accounts.map(a => ({
        id: a.id,
        name: a.name,
        city: a.city,
        country: a.country,
        region: a.region,
        latitude: a.latitude,
        longitude: a.longitude,
        primary_sponsors: a.primary_sponsors,
        primary_sponsors_id: a.primary_sponsors_id,
        secondary_sponsors: a.secondary_sponsors,
        secondary_sponsors_id: a.secondary_sponsors_id,
      })),
      allWatchlistAccounts: accounts.map(a => ({
        id: a.id,
        name: a.name,
        city: a.city,
        country: a.country,
        region: a.region,
        latitude: a.latitude,
        longitude: a.longitude,
        primary_sponsors: a.primary_sponsors,
        primary_sponsors_id: a.primary_sponsors_id,
        secondary_sponsors: a.secondary_sponsors,
        secondary_sponsors_id: a.secondary_sponsors_id,
      })),
      watchlistId,
      selectedMeasurementDate: effectiveMeasurementDate,
      latestMeasurementDate: effectiveMeasurementDate,
      digestFromDate: controlledDigestFrom,
      digestToDate: controlledDigestTo,
    }),
    [
      accounts,
      watchlistId,
      effectiveMeasurementDate,
      controlledDigestFrom,
      controlledDigestTo,
    ]
  );

  const digestAccountIdsKey = useMemo(
    () =>
      JSON.stringify(
        (metricsAccountsInput.selectedAccounts || [])
          .map(account => String(account.id || "").trim())
          .filter(Boolean)
          .sort()
      ),
    [metricsAccountsInput.selectedAccounts]
  );

  const digestDateRangeKey = useMemo(() => {
    const { digestFromDate, digestToDate } = resolveDigestDateRange(
      metricsAccountsInput,
      effectiveMeasurementDate
    );
    return `${digestFromDate || ""}|${digestToDate || ""}|${effectiveMeasurementDate || ""}`;
  }, [metricsAccountsInput, effectiveMeasurementDate]);

  const [configuration, setConfiguration] = useState(() =>
    getDefaultMetricsConfiguration()
  );
  const [digestState, setDigestState] = useState(emptyDigestState);
  const [configLoading, setConfigLoading] = useState(true);
  const [digestLoading, setDigestLoading] = useState(true);
  const [hawkeyeFetchError, setHawkeyeFetchError] = useState<string | null>(
    null
  );

  const { rowAccounts, fetchAccounts } = useMemo(
    () => normalizeAccounts(metricsAccountsInput),
    [metricsAccountsInput]
  );

  const matrixData = useMemo(
    () => buildAccountMatrixData(rowAccounts, configuration, digestState),
    [rowAccounts, configuration, digestState]
  );

  const accountsKey = useMemo(
    () => accounts.map(account => String(account.id || "").trim()).join(","),
    [accounts]
  );

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_KPI_PAGE_SIZE);
  /** Dot-by-dot reveal only the first time this Always On visit paints. */
  const [hasRevealedOnce, setHasRevealedOnce] = useState(() =>
    hasAlwaysOnRowRevealCompleted()
  );
  const [selectionStripHost, setSelectionStripHost] =
    useState<HTMLDivElement | null>(null);

  const markRevealDone = useCallback(() => {
    markAlwaysOnRowRevealCompleted();
    setHasRevealedOnce(true);
  }, []);

  useEffect(() => {
    setPage(1);
  }, [accountsKey]);

  useEffect(() => {
    if (page !== 1) markRevealDone();
  }, [page, markRevealDone]);

  // Selected accounts pin to top, then A–Z (unavailable last when toggle is on).
  const selectedIdSet = useMemo(
    () =>
      new Set(
        highlightedAccountIds.map(id => String(id || "").trim()).filter(Boolean)
      ),
    [highlightedAccountIds]
  );

  const orderedMatrixData = useMemo(() => {
    return [...matrixData].sort((left, right) => {
      const leftId = String(left?.account_id || "").trim();
      const rightId = String(right?.account_id || "").trim();
      const leftSelected = selectedIdSet.has(leftId);
      const rightSelected = selectedIdSet.has(rightId);
      if (leftSelected !== rightSelected) {
        return leftSelected ? -1 : 1;
      }
      if (showUnavailableKpiData) {
        const leftNoData = left?.hasData === false;
        const rightNoData = right?.hasData === false;
        if (leftNoData !== rightNoData) {
          return leftNoData ? 1 : -1;
        }
      }
      return accountNameAsc(left, right);
    });
  }, [matrixData, showUnavailableKpiData, selectedIdSet]);

  const totalItems = orderedMatrixData.length || accounts.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize) || 1);

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const handlePageSizeChange = (nextPageSize: number) => {
    setPageSize(nextPageSize);
    setPage(1);
  };

  // Selecting an off-page account should show it on page 1 (pinned to top).
  const selectedIdsKey = highlightedAccountIds.join(",");
  useEffect(() => {
    if (!highlightedAccountIds.length) return;
    setPage(1);
  }, [selectedIdsKey]);

  const pagedMatrixData = useMemo(() => {
    const start = (page - 1) * pageSize;
    return orderedMatrixData.slice(start, start + pageSize);
  }, [orderedMatrixData, page, pageSize]);

  const kpiDataCounts = useMemo(() => {
    let available = 0;
    let unavailable = 0;
    for (const account of matrixData) {
      if (account.hasData === false) unavailable += 1;
      else available += 1;
    }
    return { available, unavailable };
  }, [matrixData]);

  const accountsWithoutKpiDataIds = useMemo(
    () =>
      matrixData
        .filter(account => account.hasData === false)
        .map(account => String(account.account_id)),
    [matrixData]
  );

  useEffect(() => {
    if (!onKpiDataCountsChange) return;
    const loading = configLoading || digestLoading;
    if (loading) {
      // Keep the KPI availability control hidden while digests load
      onKpiDataCountsChange({ available: 0, unavailable: 0, loading: true });
      return;
    }
    onKpiDataCountsChange({ ...kpiDataCounts, loading: false });
  }, [
    kpiDataCounts,
    configLoading,
    digestLoading,
    onKpiDataCountsChange,
  ]);

  useEffect(() => {
    if (!onAccountsWithoutKpiDataChange) return;
    if (configLoading || digestLoading) {
      onAccountsWithoutKpiDataChange([]);
      return;
    }
    onAccountsWithoutKpiDataChange(accountsWithoutKpiDataIds);
  }, [
    accountsWithoutKpiDataIds,
    configLoading,
    digestLoading,
    onAccountsWithoutKpiDataChange,
  ]);

  const executiveStaffPath = orgSlug
    ? `/${orgSlug}/executive-staff?role=${ROLES.EXECUTIVE_SPONSOR_LEAD}`
    : `/executive-staff?role=${ROLES.EXECUTIVE_SPONSOR_LEAD}`;

  const kpiDataUploadPath = orgSlug
    ? `/${orgSlug}/ops?tab=data-upload`
    : `/ops?tab=data-upload`;

  const availableCategories = useMemo(
    () => getAvailableCategories(digestState.digestAiScores),
    [digestState.digestAiScores]
  );

  const orderedCategories = useMemo(
    () => getOrderedCategories(configuration, availableCategories),
    [configuration, availableCategories]
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setConfigLoading(true);
      setHawkeyeFetchError(null);
      try {
        const cfg = await loadMetricsConfigurationFromApi();
        if (!cancelled) setConfiguration(cfg);
      } catch (e) {
        console.error(e);
        if (!cancelled) setHawkeyeFetchError("Failed to load KPI configuration");
      } finally {
        if (!cancelled) setConfigLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    // Clear immediately so UI does not keep showing the previous mode's bubbles.
    setDigestState(emptyDigestState());
    setDigestLoading(true);

    let cancelled = false;
    (async () => {
      const accountIds = JSON.parse(digestAccountIdsKey || "[]") as string[];
      if (!accountIds.length) {
        if (!cancelled) {
          setDigestState(emptyDigestState());
          setDigestLoading(false);
        }
        return;
      }
      const [digestFromDate, digestToDate] = digestDateRangeKey.split("|");
      setHawkeyeFetchError(null);
      try {
        const next = await fetchHawkeyeDigestState(
          accountIds,
          digestFromDate || null,
          digestToDate || null,
          true
        );
        if (!cancelled) setDigestState(next);
      } catch (e) {
        console.warn(e);
        if (!cancelled) {
          setDigestState(emptyDigestState());
          setHawkeyeFetchError("Failed to load account digests");
        }
      } finally {
        if (!cancelled) setDigestLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [digestAccountIdsKey, digestDateRangeKey, dataModeKey]);

  const updateConfiguration = useCallback(
    (partial: Partial<MetricsConfiguration>) => {
      setConfiguration(prev => ({ ...prev, ...partial }));
    },
    []
  );

  const refetchDigests = useCallback(async () => {
    const accountIds = JSON.parse(digestAccountIdsKey || "[]") as string[];
    if (!accountIds.length) {
      setDigestState(emptyDigestState());
      return;
    }
    const [digestFromDate, digestToDate] = digestDateRangeKey.split("|");
    setDigestLoading(true);
    try {
      const next = await fetchHawkeyeDigestState(
        accountIds,
        digestFromDate || null,
        digestToDate || null,
        true
      );
      setDigestState(next);
    } catch (e) {
      console.warn(e);
      setDigestState(emptyDigestState());
    } finally {
      setDigestLoading(false);
    }
  }, [digestAccountIdsKey, digestDateRangeKey, dataModeKey]);

  const reloadConfiguration = useCallback(async () => {
    setConfigLoading(true);
    try {
      const cfg = await loadMetricsConfigurationFromApi();
      setConfiguration(cfg);
    } finally {
      setConfigLoading(false);
    }
  }, []);

  const hawkeyeMetrics: HawkeyeChartMetricsProps = useMemo(
    () => ({
      matrixData: pagedMatrixData,
      loading: configLoading || digestLoading,
      error: hawkeyeFetchError,
      configuration,
      updateConfiguration,
      availableCategories,
      orderedCategories,
      refetch: refetchDigests,
      reloadConfiguration,
    }),
    [
      pagedMatrixData,
      configLoading,
      digestLoading,
      hawkeyeFetchError,
      configuration,
      updateConfiguration,
      availableCategories,
      orderedCategories,
      refetchDigests,
      reloadConfiguration,
    ]
  );

  const handleOpenKpiConfig = () => {
    const basePath = orgSlug ? `/${orgSlug}` : "";
    // Exec Ops → Data Hub → Metrics Upload, with KPI Config open.
    navigate(`${basePath}/ops?tab=data-upload&metricsTab=weights`);
  };

  const generateDate = useMemo(() => {
    if (!isDigitalocean) return null;
    if (ingestDate && /^\d{4}-\d{2}-\d{2}$/.test(ingestDate)) return ingestDate;
    if (
      effectiveMeasurementDate &&
      /^\d{4}-\d{2}-\d{2}$/.test(effectiveMeasurementDate)
    ) {
      return effectiveMeasurementDate;
    }
    return null;
  }, [isDigitalocean, ingestDate, effectiveMeasurementDate]);

  const handleGenerateAccount = useCallback(
    async (accountId: string) => {
      if (!generateDate) return;
      const ok = await generate(
        accountId,
        generateDate,
        null,
        true,
        "openai"
      );
      if (ok) {
        setBriefedAccountIds(prev => {
          const next = new Set(prev);
          next.add(accountId);
          return next;
        });
      }
    },
    [generate, generateDate]
  );

  if (!accounts.length) {
    return (
      <Card className="m-6">
        <CardContent className="p-6 text-center max-w-lg mx-auto">
          <h2 className="text-xl font-semibold mb-2 text-slate-900">
            No accounts to show yet
          </h2>
          <p className="text-muted-foreground text-sm leading-relaxed">
            Enterprise KPIs need at least one account. Select accounts in the
            Accounts list on the left, or adjust your filters (portfolio tab,
            sponsor, or watchlist) until accounts appear—this view updates
            automatically.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div
      ref={selectionPanelRefProp ? undefined : internalPanelRef}
      className="relative isolate z-0 flex h-full min-h-0 w-full min-w-0 flex-col overflow-hidden"
    >
      <div className="shrink-0 px-4 pt-2">
        <SyntheticDataModeBanner />
      </div>
      <div className="flex w-full shrink-0 items-center justify-between gap-3 px-4 pt-2 pb-1">
        <div className="inline-flex min-w-0 flex-wrap items-center gap-3">
          <div className="inline-flex items-center gap-2 rounded-md border border-gray-200 bg-white px-2 py-1">
            <button
              type="button"
              onClick={() => setLevelView("categories")}
              className={`rounded px-2 py-1 text-xs font-medium ${
                levelView === "categories"
                  ? "bg-purple-100 text-purple-700"
                  : "text-gray-500 hover:bg-gray-50"
              }`}
            >
              Categories
            </button>
            <button
              type="button"
              onClick={() => setLevelView("metrics")}
              className={`rounded px-2 py-1 text-xs font-medium ${
                levelView === "metrics"
                  ? "bg-purple-100 text-purple-700"
                  : "text-gray-500 hover:bg-gray-50"
              }`}
            >
              Metrics
            </button>
          </div>
          {!showUnavailableKpiData && kpiDataCounts.unavailable > 0 ? (
            <div
              className="relative inline-flex items-center gap-1.5"
              onMouseEnter={openKpiUnavailableInfo}
              onMouseLeave={scheduleCloseKpiUnavailableInfo}
            >
              <span className="text-xs italic font-normal text-orange-500">
                KPIs data is currently not available for{" "}
                {kpiDataCounts.unavailable} account
                {kpiDataCounts.unavailable === 1 ? "" : "s"}.
              </span>
              <button
                type="button"
                className="inline-flex h-4 w-4 items-center justify-center rounded-full text-orange-500 hover:bg-orange-50"
                aria-label="KPI data availability info"
                aria-expanded={kpiUnavailableInfoOpen}
                onFocus={openKpiUnavailableInfo}
                onBlur={scheduleCloseKpiUnavailableInfo}
                onClick={e => {
                  e.preventDefault();
                  if (kpiUnavailableInfoOpen) {
                    scheduleCloseKpiUnavailableInfo();
                  } else {
                    openKpiUnavailableInfo();
                  }
                }}
              >
                <Info className="h-3.5 w-3.5" strokeWidth={2} />
              </button>
              {kpiUnavailableInfoOpen ? (
                <div
                  className="absolute left-0 top-full z-50 w-max max-w-none pt-1"
                  onMouseEnter={openKpiUnavailableInfo}
                  onMouseLeave={scheduleCloseKpiUnavailableInfo}
                >
                  <div className="whitespace-nowrap rounded-md border border-gray-200 bg-white px-3 py-2 text-left text-xs text-gray-700 shadow-md">
                    {canConfigureKpis ? (
                      <>
                        You can upload KPIs data for these accounts. Go to{" "}
                        <Link
                          to={kpiDataUploadPath}
                          className="font-medium text-purple-600 hover:underline"
                          onClick={() => setKpiUnavailableInfoOpen(false)}
                        >
                          Upload KPI data
                        </Link>
                        .
                      </>
                    ) : (
                      <>
                        Contact your program leader to request KPIs data (see{" "}
                        <Link
                          to={executiveStaffPath}
                          className="font-medium text-purple-600 hover:underline"
                          onClick={() => setKpiUnavailableInfoOpen(false)}
                        >
                          Executive Staff
                        </Link>
                        ).
                      </>
                    )}
                  </div>
                </div>
              ) : null}
            </div>
          ) : null}
        </div>
        {canConfigureKpis || !isDigitalocean || toolbarEnd ? (
        <div className="ml-auto inline-flex flex-nowrap items-center gap-2 rounded-md border border-gray-200 bg-white px-2 py-1">
            {toolbarEnd}
            {!isDigitalocean ? (
              <>
                <span className="shrink-0 text-xs text-gray-500">Granularity:</span>
                <select
                  value={granularity}
                  onChange={e => setGranularity(e.target.value as Granularity)}
                  className="h-7 min-w-[110px] shrink-0 rounded border border-gray-200 bg-white px-2 text-xs font-medium text-gray-700"
                  aria-label="Select granularity"
                >
                  <option value="weekly">Weekly</option>
                  <option value="monthly">Monthly</option>
                  <option value="quarterly">Quarterly</option>
                </select>
                <span className="ml-1 shrink-0 text-xs text-gray-500">Period:</span>
                <select
                  value={selectedPeriod}
                  onChange={e => setSelectedPeriod(e.target.value)}
                  onFocus={() => loadTimePeriods()}
                  className="h-7 shrink-0 rounded border border-gray-200 bg-white px-2 text-xs"
                >
                  {periodOptions.map(opt => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
                {canConfigureKpis ? (
                  <span className="h-4 w-px shrink-0 bg-gray-200" aria-hidden />
                ) : null}
              </>
            ) : null}
            {toolbarEnd && canConfigureKpis ? (
              <span className="h-4 w-px shrink-0 bg-gray-200" aria-hidden />
            ) : null}
            {canConfigureKpis ? (
              <button
                type="button"
                onClick={handleOpenKpiConfig}
                className="inline-flex shrink-0 items-center gap-1.5 rounded px-2 py-1 text-xs font-medium text-gray-700 transition-colors hover:bg-gray-50"
                title="Edit KPI configuration"
              >
                <Edit3 className="h-3.5 w-3.5" />
                Configure KPIs
              </button>
            ) : null}
        </div>
        ) : null}
      </div>

      <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
        <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
          {levelView === "metrics" ? (
            <HawkeyeMetricsBubbleChart
              selectedKPIs={selectedKPIs}
              measurementDate={effectiveMeasurementDate}
              metricsAccountsInput={metricsAccountsInput}
              hawkeyeMetrics={hawkeyeMetrics}
              highlightedAccountIds={highlightedAccountIds}
              onHighlightedAccountIdsChange={onHighlightedAccountIdsChange}
              onSaveAsWatchlist={onSaveAsWatchlist}
              selectionPanelRef={selectionPanelRef}
              showUnavailableKpiData={showUnavailableKpiData}
              enableRowReveal={!hasRevealedOnce}
              onRevealComplete={markRevealDone}
              selectionStripHost={selectionStripHost}
              dataSource={dataSource}
              generateDate={generateDate}
              generatingAccountId={generatingAccountId}
              briefedAccountIds={briefedAccountIds}
              onGenerateAccount={handleGenerateAccount}
            />
          ) : (
            <HawkeyeMatrixBubbleChart
              selectedKPIs={selectedKPIs}
              measurementDate={effectiveMeasurementDate}
              metricsAccountsInput={metricsAccountsInput}
              hawkeyeMetrics={hawkeyeMetrics}
              highlightedAccountIds={highlightedAccountIds}
              onHighlightedAccountIdsChange={onHighlightedAccountIdsChange}
              onSaveAsWatchlist={onSaveAsWatchlist}
              selectionPanelRef={selectionPanelRef}
              showUnavailableKpiData={showUnavailableKpiData}
              enableRowReveal={!hasRevealedOnce}
              onRevealComplete={markRevealDone}
              selectionStripHost={selectionStripHost}
              dataSource={dataSource}
            />
          )}
        </div>
        {totalItems > 0 ? (
          <div className="shrink-0 px-4 pb-2 pt-2">
            <Pagination
              mode="dynamic"
              currentPage={page}
              totalPages={totalPages}
              totalItems={totalItems}
              pageSize={pageSize}
              hasPrevious={page > 1}
              hasNext={page < totalPages}
              onPageChange={setPage}
              onPageSizeChange={handlePageSizeChange}
              pageSizeOptions={KPI_PAGE_SIZE_OPTIONS}
            />
          </div>
        ) : null}
        <div ref={setSelectionStripHost} className="shrink-0" />
      </div>
    </div>
  );
};

export default HawkeyePage as React.FC<HawkeyePageProps>;
